// K2-LID-CONTACT-TEST: abertura manual respeita a conexao selecionada.
import React, { useState, useEffect, useContext, useRef } from "react";
import { useHistory } from "react-router-dom";

import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";
import TextField from "@material-ui/core/TextField";
import Dialog from "@material-ui/core/Dialog";
import DialogActions from "@material-ui/core/DialogActions";
import DialogContent from "@material-ui/core/DialogContent";
import DialogTitle from "@material-ui/core/DialogTitle";
import Typography from "@material-ui/core/Typography";
import Autocomplete, { createFilterOptions } from "@material-ui/lab/Autocomplete";
import CircularProgress from "@material-ui/core/CircularProgress";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import ContactModal from "../ContactModal";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";
import { Grid, ListItemText, MenuItem, Select, Chip } from "@material-ui/core";
import { toast } from "react-toastify";
import { Facebook, Instagram, WhatsApp } from "@material-ui/icons";
import ShowTicketOpen from "../ShowTicketOpenModal";
import Alert from "@material-ui/lab/Alert";
import AlertTitle from "@material-ui/lab/AlertTitle";

const useStyles = makeStyles((theme) => ({
  online: {
    fontSize: 11,
    color: "#25d366"
  },
  offline: {
    fontSize: 11,
    color: "#e1306c"
  }
}));

const filter = createFilterOptions({
  trim: true,
});

const NewTicketModal = ({ modalOpen, onClose, initialContact, startWithContactForm = false }) => {
  const classes = useStyles();
  const history = useHistory();
  const [options, setOptions] = useState([]);
  const [channelFilter, setChannelFilter] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchParam, setSearchParam] = useState("");
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedQueue, setSelectedQueue] = useState("");
  const [selectedWhatsapp, setSelectedWhatsapp] = useState("");
  const [newContact, setNewContact] = useState({});
  const [contactDraft, setContactDraft] = useState(null);
  const [whatsapps, setWhatsapps] = useState([]);
  const [contactModalOpen, setContactModalOpen] = useState(false);
  const { user, socket } = useContext(AuthContext);
  const { companyId, whatsappId } = user;
  const isContactFirstFlowCompany = Number.isInteger(Number(companyId)) && Number(companyId) > 0;
  const showTicketRoutingFields = !isContactFirstFlowCompany || Boolean(selectedContact?.id || contactDraft);

  const [openAlert, setOpenAlert] = useState(false);
  const [userTicketOpen, setUserTicketOpen] = useState("");
  const [queueTicketOpen, setQueueTicketOpen] = useState("");

  useEffect(() => {
    if (
      modalOpen &&
      startWithContactForm &&
      isContactFirstFlowCompany &&
      !initialContact?.id
    ) {
      setNewContact({});
      setContactDraft(null);
      setSelectedContact(null);
      setSelectedQueue("");
      setSelectedWhatsapp("");
      setContactModalOpen(true);
    }
  }, [modalOpen, startWithContactForm, isContactFirstFlowCompany, initialContact]);

  // Ref para socket listener saber se alert esta ativo (evita closure stale)
  const openAlertRef = useRef(false);
  useEffect(() => { openAlertRef.current = openAlert; }, [openAlert]);

  // Quando ticket eh deletado (Resolver), libera contato em tempo real
  useEffect(() => {
    if (!socket) return;
    const event = `company-${companyId}-ticket`;
    const handler = (data) => {
      if (data.action === "delete" && openAlertRef.current) {
        setOpenAlert(false);
        setUserTicketOpen("");
        setQueueTicketOpen("");
        toast.info("Contato liberado! Clique em iniciar novamente.");
      }
    };
    socket.on(event, handler);
    return () => socket.off(event, handler);
  }, [socket, companyId]);

  // Verificar se a conexão selecionada é API Oficial
  const selectedWhatsappObj = whatsapps.find(w => w.id === selectedWhatsapp);
  const isApiOficial = selectedWhatsappObj?.channel === "whatsapp_oficial";


  useEffect(() => {
    if (initialContact?.id !== undefined) {
      setOptions([initialContact]);
      setSelectedContact(initialContact);
    }
  }, [initialContact]);

  // Verificar se o usuário pode ver todas as conexões e filas
  const canSeeAllConnections = user.profile === "admin" || user.allowConnections === "enabled";

  useEffect(() => {
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        api
          .get(`/whatsapp`, { params: { companyId, session: 0 } })
          .then(({ data }) => {
            let availableWhatsapps;
            if (canSeeAllConnections) {
              setWhatsapps(data);
              availableWhatsapps = data;
            } else {
              // Filtrar apenas as conexões vinculadas ao usuário
              const userWhatsappIds = (user.whatsapps || []).map(w => w.id);
              const filtered = userWhatsappIds.length > 0
                ? data.filter(w => userWhatsappIds.includes(w.id))
                : (user.whatsappId ? data.filter(w => w.id === user.whatsappId) : []);
              setWhatsapps(filtered);
              availableWhatsapps = filtered;
            }

            if (isContactFirstFlowCompany) {
              setSelectedWhatsapp(current => {
                if (availableWhatsapps.length === 1) return availableWhatsapps[0].id;
                return availableWhatsapps.some(w => w.id === current) ? current : "";
              });
            } else if (!canSeeAllConnections && availableWhatsapps.length === 1) {
              setSelectedWhatsapp(availableWhatsapps[0].id);
            }
          });
      };

      if (!isContactFirstFlowCompany && whatsappId !== null && whatsappId !== undefined && selectedWhatsapp === "") {
        setSelectedWhatsapp(whatsappId)
      }

      if (user.queues.length === 1) {
        setSelectedQueue(user.queues[0].id)
      }
      fetchContacts();
      setLoading(false);
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [selectedContact, channelFilter])

  useEffect(() => {
    if (!modalOpen || searchParam.length < 3) {
      setLoading(false);
      return;
    }
    setLoading(true);
    const delayDebounceFn = setTimeout(() => {
      const fetchContacts = async () => {
        try {
          const { data } = await api.get("contacts", {
            params: { searchParam },
          });
          setOptions(data.contacts);
          setLoading(false);
        } catch (err) {
          setLoading(false);
          toastError(err);
        }
      };
      fetchContacts();
    }, 500);
    return () => clearTimeout(delayDebounceFn);
  }, [searchParam, modalOpen]);

  const IconChannel = (channel) => {
    switch (channel) {
      case "facebook":
        return <Facebook style={{ color: "#3b5998", verticalAlign: "middle" }} />;
      case "instagram":
        return <Instagram style={{ color: "#e1306c", verticalAlign: "middle" }} />;
      case "whatsapp":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      case "whatsapp_oficial":
        return <WhatsApp style={{ color: "#25d366", verticalAlign: "middle" }} />
      default:
        return "error";
    }
  };

  const handleClose = () => {
    onClose();
    setSearchParam("");
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
    setSelectedContact(null);
    if (isContactFirstFlowCompany) {
      setSelectedQueue("");
      setSelectedWhatsapp("");
      setNewContact({});
      setContactDraft(null);
      setContactModalOpen(false);
      setOptions([]);
      setChannelFilter(null);
    }
  };

  const handleCloseAlert = () => {
    setOpenAlert(false);
    setLoading(false);
    setOpenAlert(false);
    setUserTicketOpen("");
    setQueueTicketOpen("");
  };

  const handleSaveTicket = async (contactId, openTemplate = false) => {
    if (!contactId && !contactDraft) return;
    if (selectedQueue === "") {
      toast.error("Selecione uma fila");
      return;
    }
    if (isContactFirstFlowCompany && selectedWhatsapp === "") {
      toast.error("Selecione uma conexão");
      return;
    }

    setLoading(true);
    let ticketRequestStarted = false;
    try {
      const queueId = selectedQueue !== "" ? selectedQueue : null;
      const whatsappId = selectedWhatsapp !== "" ? selectedWhatsapp : null;
      let ticketContactId = contactId;

      if (isContactFirstFlowCompany && contactDraft) {
        const { data: createdContact } = await api.post("/contacts", {
          ...contactDraft.contactData,
          whatsappId
        });

        ticketContactId = createdContact.id;
        setSelectedContact(createdContact);
        setOptions([createdContact]);
        setContactDraft(null);

        if (contactDraft.wallet) {
          await api.put(`/contacts/wallet/${createdContact.id}`, {
            wallets: contactDraft.wallet
          });
        }

        toast.success(i18n.t("contactModal.success"));
      }

      ticketRequestStarted = true;
      const { data: ticket } = await api.post("/tickets", {
        contactId: ticketContactId,
        queueId,
        whatsappId,
        userId: user.id,
        status: "open",
      });

      if (openTemplate && ticket?.uuid) {
        // Navegar para o ticket e abrir template automaticamente
        history.push(`/tickets/${ticket.uuid}?openTemplate=true`);
        onClose(ticket);
      } else {
        onClose(ticket);
      }
    } catch (err) {
      setLoading(false);
      
      if (err.response?.status === 403 && ticketRequestStarted) {
        const errorData = err.response.data;
        setOpenAlert(true);
        setUserTicketOpen(errorData.ticket?.user?.name || "Outro usuário");
        setQueueTicketOpen(errorData.ticket?.queue?.name || "Sem fila");
        return;
      }

      if (err.response?.status === 409 && ticketRequestStarted) {
        const ticket = JSON.parse(err.response.data.error);

        if (Number(ticket.userId) !== Number(user?.id)) {
          setOpenAlert(true);
          setUserTicketOpen(ticket?.user?.name);
          setQueueTicketOpen(ticket?.queue?.name);
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
          setLoading(false);
          if (ticket?.uuid) {
            if (openTemplate) {
              history.push(`/tickets/${ticket.uuid}?openTemplate=true`);
            } else {
              history.push(`/tickets/${ticket.uuid}`);
            }
          }
          onClose(ticket);
        }
      } else {
        toastError(err);
      }
    }
    setLoading(false);
  };

  const handleSelectOption = async (e, newValue) => {
    if (newValue?.number) {
      setSelectedContact(newValue);
      // Verificar proativamente se o contato já está em atendimento por outro usuário
      try {
        const { data } = await api.get("/tickets", {
          params: { contactId: newValue.id, status: "open", pageNumber: 1 }
        });
        const openTickets = data.tickets || [];
        const otherTicket = openTickets.find(t => Number(t.userId) !== Number(user.id));
        if (otherTicket) {
          setOpenAlert(true);
          setUserTicketOpen(otherTicket.user?.name || "Outro usuário");
          setQueueTicketOpen(otherTicket.queue?.name || "Sem fila");
        } else {
          setOpenAlert(false);
          setUserTicketOpen("");
          setQueueTicketOpen("");
        }
      } catch (err) {
        setOpenAlert(false);
      }
    } else if (newValue?.name) {
      const inputValue = newValue.name.trim();
      // Se o valor digitado parece um número de telefone, coloca no campo number
      const isPhone = /^[\d\s\+\-\(\)]{6,}$/.test(inputValue);
      if (isPhone) {
        setNewContact({ name: "", number: inputValue.replace(/\D/g, "") });
      } else {
        setNewContact({ name: inputValue });
      }
      setContactModalOpen(true);
    }
  };

  const handleCloseContactModal = () => {
    setContactModalOpen(false);
  };

  const handleAddNewContactTicket = contact => {
    if (isContactFirstFlowCompany && contact?.contactData) {
      setContactDraft(contact);
      setSelectedContact(contact.contactData);
      setOptions([contact.contactData]);
      return;
    }

    setSelectedContact(contact);
    setOptions([contact]);
  };

  const createAddContactOption = (filterOptions, params) => {
    const filtered = filter(filterOptions, params);
    if (params.inputValue !== "" && !loading && searchParam.length >= 3) {
      filtered.push({
        name: `${params.inputValue}`,
      });
    }
    return filtered;
  };

  const renderOption = option => {
    if (option.number) {
      return <>
        {IconChannel(option.channel)}
        <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
          {option.name} - {option.number}
        </Typography>
      </>
    } else {
      return `${i18n.t("newTicketModal.add")} ${option.name}`;
    }
  };

  const renderOptionLabel = option => {
    if (option.number) {
      return `${option.name} - ${option.number}`;
    } else {
      return `${option.name}`;
    }
  };

  const renderContactAutocomplete = () => {
    if (initialContact === undefined || initialContact.id === undefined) {
      return (
        <Grid xs={12} item>
          <Autocomplete
            fullWidth
            value={selectedContact}
            options={options}
            loading={loading}
            clearOnBlur
            autoHighlight
            freeSolo
            clearOnEscape
            getOptionLabel={renderOptionLabel}
            renderOption={renderOption}
            filterOptions={createAddContactOption}
            onChange={(e, newValue) => {
              if (!newValue) {
                setSelectedContact(null);
                setContactDraft(null);
                setOptions([]);
                setOpenAlert(false);
                setUserTicketOpen("");
                setQueueTicketOpen("");
                return;
              }
              setChannelFilter(newValue ? newValue.channel : "whatsapp");
              handleSelectOption(e, newValue);
            }}
            renderInput={params => (
              <TextField
                {...params}
                label={i18n.t("newTicketModal.fieldLabel")}
                variant="outlined"
                autoFocus
                onChange={e => setSearchParam(e.target.value)}
                onKeyPress={e => {
                  if (loading || !selectedContact) return;
                  else if (e.key === "Enter") {
                    handleSaveTicket(selectedContact.id);
                  }
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {loading ? (
                        <CircularProgress color="inherit" size={20} />
                      ) : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />
        </Grid>
      )
    }
    return null;
  }

  return (
    <>

      <Dialog open={modalOpen} onClose={handleClose}>
        <DialogTitle id="form-dialog-title">
          {i18n.t("newTicketModal.title")}
        </DialogTitle>
        <DialogContent dividers>
          <Grid style={{ width: 300 }} container spacing={2}>
            {/* CONTATO */}
            {renderContactAutocomplete()}
            {showTicketRoutingFields && (
              <>
                {isContactFirstFlowCompany && (
                  <Grid xs={12} item>
                    <Typography variant="body2">
                      Agora selecione a fila e a conexão para iniciar o atendimento.
                    </Typography>
                  </Grid>
                )}
            {/* FILA */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedQueue}
                onChange={(e) => {
                  setSelectedQueue(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedQueue === "") {
                    return "Selecione uma fila"
                  }
                  const queue = user.queues.find(q => q.id === selectedQueue)
                  return queue.name
                }}
              >
                {user.queues?.length > 0 &&
                  user.queues.map((queue, key) => (
                    <MenuItem dense key={key} value={queue.id}>
                      <ListItemText primary={queue.name} />
                    </MenuItem>
                  ))
                }
              </Select>
            </Grid>
            {/* CONEXAO */}
            <Grid xs={12} item>
              <Select
                required
                fullWidth
                displayEmpty
                variant="outlined"
                value={selectedWhatsapp}
                onChange={(e) => {
                  setSelectedWhatsapp(e.target.value)
                }}
                MenuProps={{
                  anchorOrigin: {
                    vertical: "bottom",
                    horizontal: "left",
                  },
                  transformOrigin: {
                    vertical: "top",
                    horizontal: "left",
                  },
                  getContentAnchorEl: null,
                }}
                renderValue={() => {
                  if (selectedWhatsapp === "") {
                    return "Selecione uma Conexão"
                  }
                  const whatsapp = whatsapps.find(w => w.id === selectedWhatsapp)
                  if (!whatsapp) return "";
                  return (
                    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                      {whatsapp.name}
                      {whatsapp.channel === "whatsapp_oficial" && (
                        <Chip size="small" label="API Oficial" style={{ backgroundColor: "#25d366", color: "#fff", height: 18, fontSize: "0.65rem" }} />
                      )}
                    </span>
                  );
                }}
              >
                {whatsapps?.length > 0 &&
                  whatsapps.map((whatsapp, key) => (
                    <MenuItem dense key={key} value={whatsapp.id}>
                      <ListItemText
                        primary={
                          <>
                            {IconChannel(whatsapp.channel)}
                            <Typography component="span" style={{ fontSize: 14, marginLeft: "10px", display: "inline-flex", alignItems: "center", lineHeight: "2" }}>
                              {whatsapp.name}
                              {whatsapp.channel === "whatsapp_oficial" && (
                                <Chip size="small" label="API Oficial" style={{ marginLeft: 8, backgroundColor: "#25d366", color: "#fff", height: 18, fontSize: "0.65rem" }} />
                              )}
                              &nbsp; <span className={(whatsapp.status) === 'CONNECTED' ? classes.online : classes.offline}>({whatsapp.status})</span>
                            </Typography>
                          </>
                        }
                      />
                    </MenuItem>
                  ))}
              </Select>
            </Grid>
              </>
            )}
          </Grid>
          {openAlert && (
            <Alert
              severity="error"
              style={{ marginTop: 12 }}
              onClose={handleCloseAlert}
            >
              <AlertTitle>Contato ja em atendimento</AlertTitle>
              {userTicketOpen && <span>Usuario: <strong>{userTicketOpen}</strong><br /></span>}
              {queueTicketOpen && <span>Fila: <strong>{queueTicketOpen}</strong></span>}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleClose}
            color="secondary"
            disabled={loading}
            variant="outlined"
          >
            {i18n.t("newTicketModal.buttons.cancel")}
          </Button>
          {showTicketRoutingFields && (isApiOficial ? (
            <ButtonWithSpinner
              variant="contained"
              type="button"
              disabled={!(selectedContact?.id || contactDraft) || openAlert}
              onClick={() => handleSaveTicket(selectedContact?.id, true)}
              style={{ backgroundColor: "#1a237e", color: "#fff" }}
              loading={loading}
            >
              Criar e Enviar Template
            </ButtonWithSpinner>
          ) : (
            <ButtonWithSpinner
              variant="contained"
              type="button"
              disabled={!(selectedContact?.id || contactDraft) || openAlert}
              onClick={() => handleSaveTicket(selectedContact?.id)}
              color="primary"
              loading={loading}
            >
              {i18n.t("newTicketModal.buttons.ok")}
            </ButtonWithSpinner>
          ))}
        </DialogActions>
        {contactModalOpen && (
          <ContactModal
            open={contactModalOpen}
            initialValues={newContact}
            onClose={handleCloseContactModal}
            onSave={handleAddNewContactTicket}
            validationWhatsappId={selectedWhatsapp}
            deferCreate={isContactFirstFlowCompany}
          ></ContactModal>
        )}

      </Dialog >
    </>
  );
};
export default NewTicketModal;
